'use client'

import { useRef, useState, type FormEvent } from 'react'
import { useRouter } from 'next/navigation'
import {
  fulfillmentRequestOfferMutation,
  parseFulfillmentRequestFailureKind,
  type FulfillmentRequestApi,
  type FulfillmentRequestFailureKind,
} from '@entities/fulfillment-request'
import { faPaperPlane, faPenToSquare, faRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Button } from '@ui/base/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Textarea } from '@ui/base/textarea'
import { typeClass } from '@utils/typography'
import axios from 'axios'
import { useForm, type UseFormSetError } from 'react-hook-form'

import { kopecksFromRubles, offerFormSchema, type OfferFormValues } from '../model/schema'

/** Ответ этого фулфилмента, если он уже отвечал: форма открывается заполненной для правки. */
const ownOffer = (request: FulfillmentRequestApi.FulfillmentRequest, fulfillmentId: string | null) =>
  request.offers.find((offer) => offer.fulfillment_id === fulfillmentId) ?? null

const rublesFromKopecks = (kopecks: number | null) => (kopecks === null ? '' : String(kopecks / 100))

const offerFieldByServerField = {
  price: 'price',
  comment: 'comment',
} as const satisfies Record<string, keyof OfferFormValues>

const getServerErrorMessage = (error: unknown) => {
  if (typeof error === 'string') {
    return error
  }

  if (error !== null && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message
  }

  return null
}

const mapOfferServerErrors = (errors: unknown): { field: keyof OfferFormValues; message: string }[] => {
  if (errors === null || typeof errors !== 'object' || Array.isArray(errors)) {
    return []
  }

  return Object.entries(errors).flatMap(([serverField, serverError]) => {
    const field = offerFieldByServerField[serverField as keyof typeof offerFieldByServerField]
    const message = getServerErrorMessage(serverError)

    return field && message ? [{ field, message }] : []
  })
}

const applyOfferFieldErrors = (error: unknown, setError: UseFormSetError<OfferFormValues>) => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  const fieldErrors = mapOfferServerErrors(error.response?.data?.errors)
  for (const fieldError of fieldErrors) {
    setError(fieldError.field, { type: 'server', message: fieldError.message })
  }

  return fieldErrors.length > 0
}

type NonConflictFailureKind = Exclude<FulfillmentRequestFailureKind, 'conflict'>

const failureMessageByKind: Record<NonConflictFailureKind, string> = {
  validation: 'Сервер не принял предложение. Проверьте цену и условия, затем отправьте ещё раз.',
  unauthorized: 'Сессия истекла. Войдите заново, откройте входящие и повторите отправку.',
  forbidden: 'Ответить на эту заявку нельзя. Вернитесь во входящие и выберите доступную заявку.',
  'not-found': 'Заявка не найдена. Обновите входящие и проверьте, осталась ли она в списке.',
  'too-many-requests': 'Слишком много попыток. Подождите несколько минут и отправьте предложение ещё раз.',
  unknown: 'Сервис временно недоступен. Проверьте соединение и отправьте предложение ещё раз позже.',
}

/**
 * Предложение фулфилмента продавцу: главное — текст с ценами и условиями, цена числом
 * необязательна и нужна лишь для быстрого сравнения в списке ответов.
 */
export const OfferForm = ({
  request,
  fulfillmentId,
  onSent,
}: {
  request: FulfillmentRequestApi.FulfillmentRequest
  fulfillmentId: string | null
  onSent?: () => void
}) => {
  const router = useRouter()
  const sentOffer = ownOffer(request, fulfillmentId)
  const [isOpen, setIsOpen] = useState(sentOffer === null)
  const [failureKind, setFailureKind] = useState<FulfillmentRequestFailureKind | null>(null)
  const isSubmitRunning = useRef(false)
  const offerRequest = useMutation(fulfillmentRequestOfferMutation())
  const commentId = `offer-comment-${request.id}`
  const commentDescriptionId = `offer-comment-description-${request.id}`
  const commentErrorId = `offer-comment-error-${request.id}`
  const priceId = `offer-price-${request.id}`
  const priceDescriptionId = `offer-price-description-${request.id}`
  const priceErrorId = `offer-price-error-${request.id}`
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<OfferFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(offerFormSchema),
    defaultValues: {
      price: rublesFromKopecks(sentOffer?.price ?? null),
      comment: sentOffer?.comment ?? '',
    },
  })

  const submitOffer = handleSubmit(async ({ price, comment }) => {
    setFailureKind(null)
    try {
      await offerRequest.mutateAsync({
        id: request.id,
        request: {
          price: kopecksFromRubles(price),
          comment: comment || null,
          expected_revision: request.revision,
        },
      })
      setIsOpen(false)
      onSent?.()
      router.refresh()
    } catch (error) {
      const nextFailureKind = parseFulfillmentRequestFailureKind(error)
      if (nextFailureKind === 'validation' && applyOfferFieldErrors(error, setError)) {
        return
      }
      setFailureKind(nextFailureKind)
    }
  })

  const send = async (event: FormEvent<HTMLFormElement>) => {
    if (isSubmitRunning.current) {
      event.preventDefault()

      return
    }
    isSubmitRunning.current = true
    try {
      await submitOffer(event)
    } finally {
      isSubmitRunning.current = false
    }
  }

  const refreshRequests = () => {
    setFailureKind(null)
    router.refresh()
  }

  const closeEdit = () => {
    setFailureKind(null)
    setIsOpen(false)
  }

  if (!isOpen) {
    return (
      <div className="grid gap-2">
        <span className={`${typeClass.supporting} text-muted-foreground`}>
          Предложение отправлено продавцу — он видит его в своей заявке вместе с вашими контактами.
        </span>
        <div>
          <Button type="button" variant="outline" onClick={() => setIsOpen(true)}>
            <FontAwesomeIcon icon={faPenToSquare} />
            Изменить предложение
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form className="grid gap-4" onSubmit={send}>
      <Field data-invalid={Boolean(errors.comment)}>
        <FieldLabel htmlFor={commentId}>Ваше предложение</FieldLabel>
        <Textarea
          id={commentId}
          rows={5}
          maxLength={2000}
          placeholder="Опишите ваше предложение: цены, что входит, сроки и условия работы"
          aria-invalid={Boolean(errors.comment)}
          aria-describedby={errors.comment ? `${commentDescriptionId} ${commentErrorId}` : commentDescriptionId}
          {...register('comment')}
        />
        <FieldDescription id={commentDescriptionId}>
          Продавец увидит этот текст и контакты вашего фулфилмента.
        </FieldDescription>
        <FieldError id={commentErrorId}>{errors.comment?.message}</FieldError>
      </Field>
      <Field className="sm:max-w-60" data-invalid={Boolean(errors.price)}>
        <FieldLabel htmlFor={priceId}>Цена, ₽ (необязательно)</FieldLabel>
        <Input
          id={priceId}
          inputMode="decimal"
          placeholder="Например, 25000"
          aria-invalid={Boolean(errors.price)}
          aria-describedby={errors.price ? `${priceDescriptionId} ${priceErrorId}` : priceDescriptionId}
          {...register('price')}
        />
        <FieldDescription id={priceDescriptionId}>
          Число помогает продавцу сравнить предложения списком.
        </FieldDescription>
        <FieldError id={priceErrorId}>{errors.price?.message}</FieldError>
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="lg" disabled={offerRequest.isPending}>
          <FontAwesomeIcon icon={faPaperPlane} />
          {offerRequest.isPending ? 'Отправляем…' : 'Сделать предложение'}
        </Button>
        {sentOffer !== null && (
          <Button type="button" variant="outline" size="lg" onClick={closeEdit}>
            <FontAwesomeIcon icon={faRotateLeft} />
            Отменить правку
          </Button>
        )}
        {failureKind !== null && failureKind !== 'conflict' && (
          <FieldError>{failureMessageByKind[failureKind]}</FieldError>
        )}
      </div>
      {failureKind === 'conflict' && (
        <Alert variant="warning">
          <AlertTitle>Заявка уже разобрана или изменилась</AlertTitle>
          <AlertDescription>
            <p>Обновите заявки и проверьте новые условия перед ответом.</p>
            <Button type="button" variant="outline" onClick={refreshRequests}>
              Обновить заявки
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </form>
  )
}
